(function () {
  const SELECTORS = {
    header: '[data-site-header]',
    search: '[data-nav-search]',
    searchOpen: '[data-nav-search-open]',
    searchClose: '[data-nav-search-close]',
    searchContainer: '[data-nav-search-container]',
    searchScroller: '[data-nav-search-scroller]',
    searchResultsContainer: '[data-nav-search-results]',
    searchInput: '[data-nav-search-input]',
    popularSearchLink: '[data-popular-search-link]',
    productsWrapper: '[data-products-wrap]',
    resultsPagination: '[data-results-pagination]',
  };

  const CLASSES = {
    pushUp: 'push-up',
    drawerOpen: 'js-drawer-open',
    cartDrawerOpen: 'js-drawer-open-cart',
    isSearching: 'is-searching',
    isSearchVisible: 'is-search-visible',
    navSearchIsVisible: 'nav-search--is-visible',
    isPaginationVisible: 'is-pagination-visible',
  };

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function getThemeSettings() {
    return window.theme?.settings || {};
  }

  function getThemeStrings() {
    return window.theme?.strings || {};
  }

  function isPreorderProduct(product) {
    const settings = getThemeSettings();

    if (!settings.showPreorderBadge) {
      return false;
    }

    const tagsJoined = `${(product.tags || []).join(',').toLowerCase()},`;

    return tagsJoined.includes('pre-order,') || tagsJoined.includes('_preorder,');
  }

  function getSavingBadgeText(product) {
    const settings = getThemeSettings();
    const strings = getThemeStrings();

    if (!settings.showSavingBadge || !product.variants?.length) {
      return '';
    }

    let price = product.price;
    let priceCompare = product.compare_at_price;
    let priceDifference = (priceCompare || 0) - price;

    if (product.variants.length > 1) {
      product.variants.forEach((variant) => {
        const variantDifference = (variant.compare_at_price || 0) - variant.price;

        if (variantDifference > priceDifference) {
          priceDifference = variantDifference;
          price = variant.price;
          priceCompare = variant.compare_at_price;
        }
      });
    }

    if (priceDifference <= 0) {
      return '';
    }

    let discount;

    if (settings.savingBadgeType === 'percentage') {
      discount = `${Math.floor((priceDifference / priceCompare) * 100)}%`;
    } else if (window.theme?.formatMoney) {
      discount = window.theme.formatMoney(priceDifference, window.theme.moneyFormat);
    } else {
      discount = (priceDifference / 100).toFixed(2);
    }

    const template =
      product.variants.length > 1 ? strings.saving_up_to_badge : strings.saving_badge;

    return (template || 'Save {{ discount }}').replace('{{ discount }}', discount);
  }

  function getProductBadges(product) {
    const settings = getThemeSettings();
    const strings = getThemeStrings();
    const tags = product.tags || [];
    const soldOut = !product.available;
    // Coming Soon needs the tag AND no stock, matching 'product-badge-state'
    const hasComingSoon = tags.includes('coming-soon') && soldOut;
    const preorder = isPreorderProduct(product);
    const savingBadgeText = getSavingBadgeText(product);
    const onSale = (product.compare_at_price || 0) > product.price;
    const badges = [];

    if (hasComingSoon && settings.comingSoonBadgeText) {
      badges.push({
        className: 'product-grid-item__tag coming-soon',
        text: settings.comingSoonBadgeText,
        style: `background-color: ${settings.comingSoonBadgeBg || '#CBC8FF'}; color: ${settings.comingSoonBadgeColor || '#000'};`,
      });
      return badges;
    }

    if (preorder) {
      // Same class as collection cards / PDP so it inherits the shared badge style
      badges.push({
        className: 'product-grid-item__tag pre-order',
        text: settings.preorderBadgeText || strings.preorder || 'Pre-order',
      });
    }

    // Pre Order + Sold Out show together, and sold out uses the card sticker
    if (settings.showSoldBadge && soldOut) {
      badges.push({
        className: 'product__badge__svg',
        sticker: true,
        text: strings.sold_out || 'Sold out',
      });
    }

    if (settings.showSaleBadge && onSale && !soldOut && !savingBadgeText) {
      badges.push({
        className: 'product-badge__item product-badge__item--sale',
        text: strings.sale_badge_text || 'Sale',
      });
    }

    if (savingBadgeText) {
      badges.push({
        className: 'product-badge__item product-badge__item--saving',
        text: savingBadgeText,
      });
    }

    return badges;
  }

  // The sold-out sticker overlays the artwork, like a collection card.
  function renderImageBadges(product) {
    const stickers = getProductBadges(product).filter((badge) => badge.sticker);

    if (!stickers.length) {
      return '';
    }

    const svg = getThemeSettings().soldOutStickerSvg || '';
    const items = stickers
      .map((badge) => `<div class="${badge.className}" aria-label="${escapeHtml(badge.text)}">${svg}</div>`)
      .join('');

    return `<div class="product-badge" data-product-badge>${items}</div>`;
  }

  // Pre Order / Coming Soon read as text tags under the title, as on cards.
  function renderTitleBadges(product) {
    const tags = getProductBadges(product).filter((badge) => !badge.sticker);

    if (!tags.length) {
      return '';
    }

    const items = tags
      .map((badge) => {
        const styleAttr = badge.style ? ` style="${badge.style}"` : '';

        return `<span class="${badge.className}"${styleAttr}>${escapeHtml(badge.text)}</span>`;
      })
      .join('');

    return `<p class="result__tags">${items}</p>`;
  }

  function NavSearch(container) {
    this.container = container;
    this.searchInput = container.querySelector(SELECTORS.searchInput);
    this.searchClose = container.querySelector(SELECTORS.searchClose);
    this.searchContainer = container.querySelector(SELECTORS.searchContainer);
    this.popularLinks = container.querySelectorAll(SELECTORS.popularSearchLink);
    this.scrollableElement = container.querySelector(SELECTORS.searchScroller);
    this.searchResultsContainer = container.querySelector(SELECTORS.searchResultsContainer);
    this.resultsPagination = container.querySelector(SELECTORS.resultsPagination);
    this.productsWrapper = container.querySelector(SELECTORS.productsWrapper);
    this.searchOpen = document.querySelectorAll(SELECTORS.searchOpen);
    this.header = document.querySelector(SELECTORS.header);
    this.noResultsTemplate = container.dataset.noResults || 'No results';
    this.openSearchTimeout = 0;
    this.linkOpened = null;
    this.result = null;

    this.init();
  }

  NavSearch.prototype.init = function () {
    if (!this.searchInput || !this.productsWrapper) {
      return;
    }

    this.bindOpenTriggers();
    this.bindClose();
    this.bindPopularLinks();
    this.bindInput();
    this.bindEscape();
  };

  NavSearch.prototype.bindOpenTriggers = function () {
    this.searchOpen.forEach((trigger) => {
      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.linkOpened = trigger;
          this.open();
        },
        true
      );
    });
  };

  NavSearch.prototype.bindClose = function () {
    if (!this.searchClose) {
      return;
    }

    this.searchClose.addEventListener('click', (event) => {
      event.preventDefault();
      this.close();
    });
  };

  NavSearch.prototype.bindPopularLinks = function () {
    this.popularLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const searchText = link.textContent.trim();
        const searchUrl = window.theme?.routes?.searchUrl || '/search';

        window.location.href = `${searchUrl}?type=product&q=${encodeURIComponent(searchText)}&options[prefix]=last`;
      });
    });
  };

  NavSearch.prototype.bindInput = function () {
    const debounce = window.theme?.debounce || ((fn, time) => {
      let timeout;

      return function debounced(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), time);
      };
    });

    this.searchInput.addEventListener(
      'input',
      debounce((event) => {
        const value = event.target.value.trim();

        if (value.length > 1) {
          this.searchContainer.classList.add(CLASSES.isSearching, CLASSES.pushUp);
          this.fetchProductSuggestions(value);
        } else {
          this.reset();
        }
      }, 300)
    );
  };

  NavSearch.prototype.bindEscape = function () {
    this.container.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        event.stopImmediatePropagation();
        this.close();
      }
    });
  };

  NavSearch.prototype.open = function () {
    const body = document.body;
    const isDrawerOpen = body.classList.contains(CLASSES.drawerOpen) || body.classList.contains(CLASSES.cartDrawerOpen);
    const openDrawer = document.querySelector('drawer-element.is-open');

    if (isDrawerOpen || openDrawer) {
      if (openDrawer && typeof openDrawer.close === 'function') {
        openDrawer.close();
      }

      if (this.openSearchTimeout) {
        clearTimeout(this.openSearchTimeout);
      }

      this.openSearchTimeout = window.setTimeout(() => this.openSearch(), 400);
      return;
    }

    this.openSearch();
  };

  NavSearch.prototype.openSearch = function () {
    if (!this.header) {
      return;
    }

    this.header.classList.add(CLASSES.isSearchVisible);
    this.container.classList.add(CLASSES.navSearchIsVisible);
    this.container.setAttribute('aria-expanded', 'true');

    document.dispatchEvent(
      new CustomEvent('theme:scroll:lock', {
        bubbles: true,
        detail: this.scrollableElement,
      })
    );

    window.setTimeout(() => {
      window.theme?.a11y?.trapFocus?.({
        container: this.container,
        elementToFocus: this.searchInput,
      });
      this.searchInput.focus();
    }, 100);
  };

  NavSearch.prototype.close = function () {
    if (!this.header || !this.header.classList.contains(CLASSES.isSearchVisible)) {
      return;
    }

    this.reset();

    window.theme?.a11y?.removeTrapFocus?.({
      container: this.container,
    });

    this.header.classList.remove(CLASSES.isSearchVisible);
    this.container.classList.remove(CLASSES.navSearchIsVisible);
    this.container.setAttribute('aria-expanded', 'false');

    window.setTimeout(() => {
      if (this.linkOpened) {
        window.theme?.a11y?.trapFocus?.({
          container: this.header,
          elementToFocus: this.linkOpened,
        });
      }
    }, 100);

    window.setTimeout(() => {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }, 250);
  };

  NavSearch.prototype.reset = function (clearTerms = true) {
    if (this.productsWrapper) {
      this.productsWrapper.innerHTML = '';
    }

    if (clearTerms && this.searchInput) {
      this.searchInput.value = '';
    }

    this.searchContainer?.classList.remove(CLASSES.isSearching, CLASSES.pushUp);
    this.searchResultsContainer?.classList.remove(CLASSES.isPaginationVisible);
  };

  NavSearch.prototype.fetchProductSuggestions = function (terms) {
    fetch(
      `/search/suggest.json?q=${encodeURIComponent(terms)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error('Search request failed');
        }

        return response.json();
      })
      .then((response) => {
        this.result = response.resources.results;
        return this.fetchProducts(this.result.products);
      })
      .then((html) => {
        this.productsWrapper.innerHTML = '';
        this.searchResultsContainer.classList.remove(CLASSES.isPaginationVisible);

        if (!html) {
          this.productsWrapper.innerHTML = this.renderNoResult(terms);
        } else {
          this.productsWrapper.innerHTML = html;

          if (this.result.products.length > 9) {
            const paginationLink = this.resultsPagination?.querySelector('a');

            if (paginationLink) {
              const searchUrl = window.theme?.routes?.searchUrl || '/search';
              paginationLink.href = `${searchUrl}?type=product&q=${encodeURIComponent(terms)}&options[prefix]=last`;
            }

            this.searchResultsContainer.classList.add(CLASSES.isPaginationVisible);
          }
        }

        this.searchContainer.classList.remove(CLASSES.isSearching);
      })
      .catch((error) => {
        console.error(error);
        this.searchContainer.classList.remove(CLASSES.isSearching);
      });
  };

  NavSearch.prototype.fetchProducts = function (products) {
    if (!products || !products.length) {
      return Promise.resolve('');
    }

    const requests = products.map((product) =>
      fetch(`/products/${product.handle}.js`)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Product request failed');
          }

          return response.json();
        })
        .then((productJSON) => this.renderProduct(productJSON))
    );

    return Promise.all(requests).then((items) => items.join(''));
  };

  NavSearch.prototype.renderNoResult = function (terms) {
    const text = this.noResultsTemplate.replace('{{ terms }}', escapeHtml(terms));

    return `<li class="result result--full"><p class="result__title">${text}</p></li>`;
  };

  NavSearch.prototype.renderProduct = function (product) {
    const title = product.title.replace(/(<([^>]+)>)/gi, '');
    const image = product.featured_image || window.theme?.assets?.no_image || '';
    const imageBadgesMarkup = renderImageBadges(product);
    const titleBadgesMarkup = renderTitleBadges(product);
    const imageMarkup = image
      ? `<img class="result__image-img" src="${image}" alt="${escapeHtml(title)}" loading="lazy" width="400" height="400">`
      : '';

    return `<li class="result">
      <div class="result__image">
        <a href="${product.url}" class="result__image-link" aria-label="${escapeHtml(title)}">
          ${imageMarkup}
          ${imageBadgesMarkup}
        </a>
      </div>
      <p class="result__title">
        <a href="${product.url}">${escapeHtml(title)}</a>
      </p>
      ${titleBadgesMarkup}
    </li>`;
  };

  function initNavSearch() {
    const container = document.querySelector(SELECTORS.search);

    if (!container || container.dataset.navSearchInitialized === 'true') {
      return;
    }

    container.dataset.navSearchInitialized = 'true';
    new NavSearch(container);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNavSearch);
  } else {
    initNavSearch();
  }
})();
